import { BridgeKycAssociatedPersonRequirement, BridgeKycInfoData } from 'services/merchants/types';

import { useBridgeKycInfoContext } from '../../BridgeKycInfoContext';
import { BadgeRow, CompleteBadge, CompleteIcon, ErrorBadge } from './styles';

enum BridgeFieldStatus {
  Complete = 'complete',
  Missing = 'missing',
  Issue = 'issue',
}

const isPersonRequirement = (f: string | BridgeKycAssociatedPersonRequirement): f is BridgeKycAssociatedPersonRequirement =>
  typeof f !== 'string';

type Requirement = string | BridgeKycAssociatedPersonRequirement;

const resolveStatus = (
  requirements: BridgeKycInfoData['requirements'],
  predicate: (f: Requirement) => boolean,
): BridgeFieldStatus | null => {
  if (!requirements) return null;
  if (requirements.complete.some(predicate)) return BridgeFieldStatus.Complete;
  if (requirements.missing.some(predicate)) return BridgeFieldStatus.Missing;
  if (requirements.issues.some(predicate)) return BridgeFieldStatus.Issue;
  return null;
};

const getFieldStatus = (fieldName: string, info: BridgeKycInfoData | null | undefined): BridgeFieldStatus | null =>
  resolveStatus(info?.requirements ?? null, (f) => f === fieldName);

const getPersonFieldStatus = (
  fieldName: string,
  personUuid: string | undefined,
  info: BridgeKycInfoData | null | undefined,
): BridgeFieldStatus | null => {
  if (!personUuid) return null;
  return resolveStatus(
    info?.requirements ?? null,
    (f) => isPersonRequirement(f) && f.associatedPerson === personUuid && f.items.includes(fieldName),
  );
};

const StatusBadge = ({ status }: { status: BridgeFieldStatus }) => (
  <BadgeRow>
    {status === BridgeFieldStatus.Complete && (
      <CompleteBadge>
        <CompleteIcon />
      </CompleteBadge>
    )}
    {status === BridgeFieldStatus.Missing && <ErrorBadge>✕ Missing</ErrorBadge>}
    {status === BridgeFieldStatus.Issue && <ErrorBadge>✕ Issue</ErrorBadge>}
  </BadgeRow>
);

interface FieldStatusBadgeProps {
  fieldName: string;
}

export const FieldStatusBadge = ({ fieldName }: FieldStatusBadgeProps) => {
  const kycInfo = useBridgeKycInfoContext();
  const status = getFieldStatus(fieldName, kycInfo);
  if (!status) return null;
  return <StatusBadge status={status} />;
};

interface PersonFieldStatusBadgeProps {
  fieldName: string;
  personUuid: string | undefined;
}

export const PersonFieldStatusBadge = ({ fieldName, personUuid }: PersonFieldStatusBadgeProps) => {
  const kycInfo = useBridgeKycInfoContext();
  const status = getPersonFieldStatus(fieldName, personUuid, kycInfo);
  if (!status) return null;
  return <StatusBadge status={status} />;
};
